import React from 'react';
import { Link } from 'react-router-dom';
import {
  Calendar, Globe, Bell, CreditCard, Users, Shield, ArrowRight,
  Check, X, Phone, Mail, MessageSquare, Zap, Clock, Settings,
  FileText, UserCheck, Headphones, BarChart3, Database,
  Users2, Smartphone
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { faqs, globalCTA, keyFeatures } from '../content';
import { useInView } from 'react-intersection-observer';
import HeroSection from '../components/HeroSection';
import Seo from '../components/Seo';

const iconMap = {
  Calendar, Globe, Bell, CreditCard, FileText, Users, UserCheck,
  Shield, Users2, Headphones, Settings, Smartphone
};

const Solution = () => {
  const { ref: featuresRef, inView: featuresInView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { ref: whyRef, inView: whyInView } = useInView({ threshold: 0.1, triggerOnce: true });
  const { ref: compareRef, inView: compareInView } = useInView({ threshold: 0.1, triggerOnce: true });

  const faqPageJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer }
    }))
  };

  const integrations = [
    { name: 'Google Agenda', icon: Calendar },
    { name: 'Outlook', icon: Mail },
    { name: 'Apple Calendar', icon: Calendar },
    { name: 'Zoom', icon: Phone },
    { name: 'Google Meet', icon: MessageSquare },
    { name: 'Google Analytics', icon: BarChart3 },
    { name: 'Stripe / Paiements', icon: CreditCard },
    { name: 'Zapier / API', icon: Zap },
    { name: 'Mailchimp', icon: Mail },
    { name: 'Google Tag Manager', icon: Settings },
    { name: 'WordPress', icon: Globe },
    { name: 'Facebook Pixel', icon: Database },
  ];

  const comparisonRows = [
    { feature: 'Disponibilité', manual: 'Horaires bureau uniquement', ea: '24h/24, 7j/7' },
    { feature: 'Rappels clients', manual: 'Manuelle (appel, SMS)', ea: 'Automatiques (email, SMS, WhatsApp)' },
    { feature: 'Annulations / reports', manual: 'Gestion chronophage', ea: 'Client autonome en ligne' },
    { feature: 'Paiements & acomptes', manual: 'Virement manuel ou espèces', ea: 'En ligne, sécurisé, automatique' },
    { feature: 'Suivi clients', manual: 'Agenda papier ou tableur', ea: 'Fiche client centralisée, historique' },
    { feature: 'Image professionnelle', manual: 'Outil générique visible', ea: 'À votre nom, vos couleurs' },
    { feature: 'Temps administratif', manual: '2h+ par semaine', ea: 'Moins de 15 min/semaine' },
  ];

  const whyPoints = [
    {
      icon: Zap,
      title: 'Installation clé en main',
      description: "Nous configurons tout pour vous : agenda, services, personnalisation visuelle, intégration sur votre site. Vous démarrez sans stress et sans compétences techniques requises."
    },
    {
      icon: UserCheck,
      title: 'Personnalisé à votre image',
      description: "Votre logo, vos couleurs, votre nom de domaine. Vos clients ne voient que vous — aucune mention d'un outil tiers. Une expérience cohérente et professionnelle du premier au dernier clic."
    },
    {
      icon: Headphones,
      title: 'Support humain, réactif et durable',
      description: "Une question après 3 mois ? Vous nous écrivez, on vous répond. Urgences traitées sous 6h. Nous ne disparaissons pas après l'installation."
    },
    {
      icon: Shield,
      title: 'Sécurité & conformité RGPD',
      description: "Hébergement sécurisé, données chiffrées, gestion des consentements et des droits clients. Votre pratique est protégée, vos données restent les vôtres."
    },
    {
      icon: Settings,
      title: 'Adapté à votre pratique',
      description: "Durées de séance, motifs de consultation, règles de réservation spécifiques, questionnaires préalables — nous configurons selon votre fonctionnement, pas l'inverse."
    },
    {
      icon: Clock,
      title: 'Évolutif avec vous',
      description: "Vous démarrez simple et évoluez à votre rythme. Activation de nouvelles fonctionnalités, changement d'offre, ajout d'un praticien — tout est possible sans contrainte."
    },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F9F6F0' }}>
      <Seo
        title="La solution de réservation en ligne | Espace Agenda"
        description="Réservation en ligne, rappels automatiques, paiements et fiche client : une plateforme installée et configurée pour votre pratique. Support humain inclus."
        jsonLd={faqPageJsonLd}
      />
      <HeroSection
        title="Tout ce qu'il faut pour gérer vos rendez-vous —"
        titleHighlight="à votre nom"
        description="Plateforme installée et configurée pour votre pratique : réservation en ligne, rappels automatiques, paiements et support humain inclus."
        backgroundImage="/hero-solution.webp"
        imagePosition="center 22%"
        ctaText={globalCTA.primary}
      />

      {/* Comment ça se passe */}
      <section className="py-20" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <span className="label-tag mb-4 block" style={{ color: '#7D6235' }}>Le processus</span>
            <h2 className="text-3xl font-bold font-heading tracking-tight sm:text-4xl" style={{ color: '#2C352D' }}>
              Comment ça se passe ?
            </h2>
            <p className="mt-4 text-lg" style={{ color: '#5E6C60' }}>
              Trois étapes, puis vous n'avez plus qu'à accueillir vos clients.
            </p>
          </div>

          <div className="mx-auto mt-16 max-w-5xl">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  step: '1',
                  title: 'Cadrage (15-30 min)',
                  description: "Nous discutons de vos besoins, vos horaires, vos services, vos règles. Un formulaire détaillé pour tout paramétrer selon votre pratique."
                },
                {
                  step: '2',
                  title: 'Installation + paramétrage',
                  description: "Nous configurons votre plateforme de A à Z. Personnalisation visuelle, intégration sur votre site, activation des rappels et paiements selon votre offre."
                },
                {
                  step: '3',
                  title: 'Formation + support',
                  description: "Une mini-session pour vous expliquer l'essentiel. Puis notre équipe reste disponible pour toutes vos questions et évolutions."
                }
              ].map((item) => (
                <Card
                  key={item.step}
                  className="rounded-3xl transition-all duration-300 hover:shadow-warm-md"
                  style={{ border: '1px solid #E2DFD8', backgroundColor: '#FFFFFF' }}
                >
                  <CardContent className="pt-6">
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-full text-white text-xl font-bold font-heading shadow-md mb-6"
                      style={{ backgroundColor: '#5A7161' }}
                    >
                      {item.step}
                    </div>
                    <h3 className="text-xl font-semibold font-heading mb-3" style={{ color: '#2C352D' }}>{item.title}</h3>
                    <p style={{ color: '#5E6C60' }}>{item.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ce que vous obtenez — Fonctionnalités complètes */}
      <section ref={featuresRef} className="py-20" style={{ backgroundColor: '#F4F0E8' }}>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <span className="label-tag mb-4 block" style={{ color: '#7D6235' }}>Fonctionnalités</span>
            <h2 className="text-3xl font-bold font-heading tracking-tight sm:text-4xl" style={{ color: '#2C352D' }}>
              Ce que vous obtenez
            </h2>
            <p className="mt-4 text-lg" style={{ color: '#5E6C60' }}>
              Une solution installée, configurée et maintenue pour votre pratique.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {keyFeatures.map((feature, index) => {
              const Icon = iconMap[feature.icon] || Calendar;
              return (
                <Card
                  key={feature.id}
                  className={`rounded-2xl transition-all duration-500 hover:shadow-warm-md ${
                    featuresInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{
                    transitionDelay: featuresInView ? `${index * 60}ms` : '0ms',
                    border: '1px solid #E2DFD8',
                    backgroundColor: '#FFFFFF'
                  }}
                >
                  <CardContent className="pt-6">
                    <div className="flex gap-4 items-start">
                      <div className="flex-shrink-0">
                        <div
                          className="flex h-10 w-10 items-center justify-center rounded-lg"
                          style={{ backgroundColor: 'rgba(90,113,97,0.10)', color: '#5A7161' }}
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                      </div>
                      <div>
                        <h3 className="text-base font-semibold" style={{ color: '#2C352D' }}>{feature.title}</h3>
                        <p className="mt-2 text-sm" style={{ color: '#5E6C60' }}>{feature.description}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Avec ou sans Espace Agenda */}
      <section ref={compareRef} className="py-20" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="mx-auto max-w-5xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-12">
            <h2 className="text-3xl font-bold font-heading tracking-tight sm:text-4xl" style={{ color: '#2C352D' }}>
              Avec ou sans Espace Agenda
            </h2>
            <p className="mt-4 text-lg" style={{ color: '#5E6C60' }}>
              Ce que ça change concrètement dans votre semaine.
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse rounded-xl overflow-hidden shadow-md">
              <thead>
                <tr>
                  <th className="text-left py-4 px-6 font-medium text-sm w-1/3" style={{ backgroundColor: '#F4F0E8', color: '#5E6C60' }}>Aspect</th>
                  <th className="text-center py-4 px-6 font-medium text-sm w-1/3" style={{ backgroundColor: '#F4F0E8', color: '#5E6C60' }}>
                    <span className="flex items-center justify-center gap-2">
                      <X className="h-4 w-4 text-red-400" />
                      Gestion manuelle
                    </span>
                  </th>
                  <th className="text-center py-4 px-6 font-medium text-sm w-1/3" style={{ backgroundColor: '#5A7161', color: '#FFFFFF' }}>
                    <span className="flex items-center justify-center gap-2">
                      <Check className="h-4 w-4" />
                      Avec Espace Agenda
                    </span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {comparisonRows.map((row, index) => (
                  <tr key={index} className={`border-b border-gray-100 ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}`}>
                    <td className="py-4 px-6 font-medium text-sm" style={{ color: '#2C352D' }}>{row.feature}</td>
                    <td className="py-4 px-6 text-center text-red-500 text-sm">{row.manual}</td>
                    <td className="py-4 px-6 text-center text-sm font-medium" style={{ color: '#5A7161', backgroundColor: 'rgba(90,113,97,0.05)' }}>{row.ea}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pourquoi nous choisir */}
      <section ref={whyRef} className="py-20" style={{ backgroundColor: '#F4F0E8' }}>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-16">
            <span className="label-tag mb-4 block" style={{ color: '#7D6235' }}>Notre différence</span>
            <h2 className="text-3xl font-bold font-heading tracking-tight sm:text-4xl" style={{ color: '#2C352D' }}>
              Pourquoi nous choisir ?
            </h2>
            <p className="mt-4 text-lg" style={{ color: '#5E6C60' }}>
              Pas un logiciel à installer vous-même. Un service humain, de l'installation au quotidien.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {whyPoints.map((point, index) => {
              const Icon = point.icon;
              return (
                <div
                  key={index}
                  className={`flex gap-4 transition-all duration-500 ${
                    whyInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                  }`}
                  style={{ transitionDelay: whyInView ? `${index * 80}ms` : '0ms' }}
                >
                  <div className="flex-shrink-0">
                    <div
                      className="flex h-12 w-12 items-center justify-center rounded-xl shadow-sm"
                      style={{ backgroundColor: '#5A7161', color: '#FFFFFF' }}
                    >
                      <Icon className="h-6 w-6" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold mb-2" style={{ color: '#2C352D' }}>{point.title}</h3>
                    <p className="text-sm leading-relaxed" style={{ color: '#5E6C60' }}>{point.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Vos outils habituels, connectés */}
      <section className="py-20" style={{ backgroundColor: '#FFFFFF' }}>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center mb-12">
            <span className="label-tag mb-4 block" style={{ color: '#7D6235' }}>Intégrations</span>
            <h2 className="text-3xl font-bold font-heading tracking-tight sm:text-4xl" style={{ color: '#2C352D' }}>
              Vos outils habituels, connectés
            </h2>
            <p className="mt-4 text-lg" style={{ color: '#5E6C60' }}>
              Espace Agenda s'intègre à ce que vous utilisez déjà — sans configuration de votre part.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 max-w-5xl mx-auto">
            {integrations.map((integration, index) => {
              const Icon = integration.icon;
              return (
                <div
                  key={index}
                  className="flex flex-col items-center gap-2 p-4 rounded-2xl border transition-all duration-300 hover:shadow-warm-md hover:-translate-y-1"
                  style={{ backgroundColor: '#F4F0E8', borderColor: '#E2DFD8' }}
                >
                  <div
                    className="h-9 w-9 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'rgba(90,113,97,0.12)' }}
                  >
                    <Icon className="h-5 w-5" style={{ color: '#5A7161' }} />
                  </div>
                  <span className="text-xs font-medium text-center leading-tight" style={{ color: '#2C352D' }}>{integration.name}</span>
                </div>
              );
            })}
          </div>

          <p className="text-center text-sm mt-8" style={{ color: '#5E6C60' }}>
            Et bien d'autres selon votre offre. Intégrations spécifiques disponibles sur devis.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="py-20 scroll-mt-20" style={{ backgroundColor: '#F4F0E8' }}>
        <div className="mx-auto max-w-4xl px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold font-heading tracking-tight sm:text-4xl" style={{ color: '#2C352D' }}>
              Questions fréquentes
            </h2>
            <p className="mt-4 text-lg" style={{ color: '#5E6C60' }}>
              Les réponses aux questions que tout le monde se pose.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((faq) => (
              <AccordionItem
                key={faq.id}
                value={faq.id}
                className="rounded-2xl px-6"
                style={{ backgroundColor: '#FFFFFF', border: '1px solid #E2DFD8' }}
              >
                <AccordionTrigger
                  className="text-left font-semibold py-5 hover:no-underline"
                  style={{ color: '#2C352D' }}
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent
                  className="leading-relaxed pb-5"
                  style={{ color: '#5E6C60' }}
                >
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="text-center mt-12">
            <p className="mb-4" style={{ color: '#5E6C60' }}>Une question spécifique à votre pratique ?</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/contact">
                <Button
                  className="rounded-full font-medium px-6"
                  style={{ backgroundColor: '#5A7161', color: '#FFFFFF' }}
                >
                  Posez-la nous directement
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link
                to="/blog"
                className="inline-flex items-center justify-center gap-2 rounded-full px-6 py-2.5 text-sm font-medium transition-colors"
                style={{ borderColor: '#5A7161', color: '#5A7161', border: '2px solid' }}
                data-testid="solution-faq-blog-link"
              >
                Lire nos conseils sur le blog
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 relative overflow-hidden" style={{ backgroundColor: '#2C352D' }}>
        <div className="absolute inset-0 opacity-30"
          style={{ background: 'radial-gradient(ellipse at 30% 50%, rgba(90,113,97,0.6) 0%, transparent 60%)' }} />
        <div className="relative z-10 mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h2 className="font-heading font-medium text-4xl sm:text-5xl mb-6" style={{ color: '#F9F6F0' }}>
              Votre agenda en ligne,<br />prêt en 7 jours.
            </h2>
            <p className="text-lg leading-relaxed mb-10" style={{ color: 'rgba(249,246,240,0.75)' }}>
              Échangeons 20 minutes. Vous expliquez votre pratique, nous vous montrons à quoi ressemblera votre page.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact">
                <Button
                  size="lg"
                  className="rounded-full font-medium px-8"
                  style={{ backgroundColor: '#F9F6F0', color: '#2C352D' }}
                >
                  {globalCTA.primary}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/offres">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8"
                  style={{ borderColor: 'rgba(249,246,240,0.3)', color: '#F9F6F0' }}
                >
                  Voir les offres
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solution;
